import { Landmark, Moon, Sun } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { EMAIL_DOMAIN, sanitizeUsername } from "@/lib/format";

export const PageHeader = ({ title, action }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 animate-rise">
    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="page-title">{title}</h1>
    {action}
  </div>
);

export const BrandMark = ({ size = "h-10 w-10", className }) => {
  const { logoSrc, settings } = useSettings();
  if (logoSrc) return <img src={logoSrc} alt={settings.app_name} className={cn(size, "object-contain rounded-xl", className)} data-testid="brand-logo" />;
  return (
    <div className={cn(size, "rounded-xl bg-primary text-primary-foreground grid place-items-center", className)} data-testid="brand-logo-fallback">
      <Landmark className="h-1/2 w-1/2" />
    </div>
  );
};

export const ThemeToggle = ({ className }) => {
  const { dark, toggleMode } = useSettings();
  return (
    <button
      type="button"
      onClick={toggleMode}
      aria-label="Ganti tema"
      data-testid="topbar-theme-toggle"
      className={cn("h-11 w-11 rounded-full border bg-card grid place-items-center hover:bg-muted transition-colors", className)}
    >
      {dark ? <Sun className="h-5 w-5 text-amber-400" /> : <Moon className="h-5 w-5" />}
    </button>
  );
};

export const StatCard = ({ label, value, hint, icon: Icon, testId, tone = "primary" }) => (
  <div className="stat-card" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <p className="eyebrow">{label}</p>
      {Icon && (
        <span className={cn("h-10 w-10 rounded-xl grid place-items-center", tone === "primary" ? "bg-primary/12 text-primary" : "bg-muted text-foreground")}>
          <Icon className="h-5 w-5" />
        </span>
      )}
    </div>
    <p className="font-mono text-xl sm:text-2xl font-semibold mt-4 tracking-tight" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testId }) => (
  <div className="rounded-2xl border border-dashed p-10 sm:p-14 text-center bg-card/50" data-testid={testId}>
    {Icon && <Icon className="h-12 w-12 mx-auto text-muted-foreground/60" />}
    <h3 className="font-heading text-lg font-semibold mt-4">{title}</h3>
    {text && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const StatusPill = ({ active, testId }) => (
  <span
    data-testid={testId}
    className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
      active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground")}
  >
    <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-primary" : "bg-muted-foreground")} />
    {active ? "Aktif" : "Non-aktif"}
  </span>
);

export const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium">{label}</label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export const UsernameInput = ({ value, onChange, testId, required = false, disabled = false }) => (
  <div className={cn("flex items-stretch rounded-md border border-input bg-background overflow-hidden transition-colors focus-within:ring-2 focus-within:ring-ring", disabled && "opacity-60")}>
    <input
      value={value}
      onChange={(e) => onChange(sanitizeUsername(e.target.value))}
      required={required}
      disabled={disabled}
      autoComplete="off"
      spellCheck={false}
      placeholder="nama_akun"
      data-testid={testId}
      className="flex-1 min-w-0 h-11 bg-transparent px-3 text-sm outline-none"
    />
    <span className="grid place-items-center px-3 text-sm text-muted-foreground bg-muted border-l select-none whitespace-nowrap" data-testid={testId ? `${testId}-suffix` : undefined}>@{EMAIL_DOMAIN}</span>
  </div>
);
